/**
 * Transcription Service
 * API client for transcription feature endpoints
 */

import { REACT_APP_API_TOKEN } from '@env';
import { Platform } from 'react-native';

import { getApiConfig } from '../config/api';
import { getAuthService } from './auth/authServiceFactory';

import type {
  CreateRequestWithImportData,
  CreateRequestWithImportResponse,
  TranscribeExistingRequestData,
  JobStatusResponse,
  TranscriptionError,
} from '../types/transcription';

class TranscriptionService {
  private getApiBaseUrl(): string {
    return getApiConfig().BASE_URL;
  }
  /**
   * Create request with file import
   * Uploads file and creates request in PROCESSING stage
   */
  async createRequestWithImport(
    data: CreateRequestWithImportData,
  ): Promise<CreateRequestWithImportResponse> {
    try {
      // Create FormData for multipart/form-data request
      const formData = new FormData();
      formData.append('customerName', data.customerName);
      formData.append('region', data.region);
      formData.append('requestorName', data.requestorName);
      formData.append('requestorEmail', data.requestorEmail);

      // Append file - use webFile for web platform, otherwise use uri/name/type for mobile
      if (data.file.webFile) {
        // Web platform: use actual File object
        formData.append('file', data.file.webFile);
        formData.append('source', 'web'); // Web uses AWS Batch transcription
      } else {
        // Mobile platform: React Native FormData requires specific format
        // The file object must be cast to any because RN's FormData types don't match web
        const fileForUpload: any = {
          uri: data.file.uri,
          name: data.file.name,
          type: data.file.type,
        };
        formData.append('file', fileForUpload);
        // Mobile: source defaults to null (backward compatible with old mobile apps using streaming)
      }

      // Get JWT token for authorization from the configured auth service
      const authService = getAuthService();
      const jwtToken = await authService.getToken();

      // Prepare headers
      const headers: Record<string, string> = {
        // Don't set Content-Type for multipart/form-data - let fetch set it with boundary
      };

      // Add JWT Authorization header if token exists
      if (jwtToken) {
        headers.Authorization = `Bearer ${jwtToken}`;
      }

      // Add API token if configured (for QA/Production environments)
      if (REACT_APP_API_TOKEN) {
        headers['x-api-token'] = REACT_APP_API_TOKEN;
      }

      // Use fetch instead of axios for better React Native compatibility
      const response = await fetch(
        `${this.getApiBaseUrl()}/api/transcribe/create-with-import`,
        {
          method: 'POST',
          headers,
          credentials: 'include', // Include session cookies (for web)
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            errorData.error ||
            'Failed to create request with import',
        );
      }

      const result: CreateRequestWithImportResponse = await response.json();
      return result;
    } catch (error) {
      console.error('❌ createRequestWithImport error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error: Unable to create request');
    }
  }

  /**
   * Get transcription job status by request ID
   * Used for polling job progress
   */
  async getJobStatus(requestId: number): Promise<JobStatusResponse> {
    try {
      // Get JWT token for authorization from the configured auth service
      const authService = getAuthService();
      const jwtToken = await authService.getToken();

      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add JWT Authorization header if token exists
      if (jwtToken) {
        headers.Authorization = `Bearer ${jwtToken}`;
      }

      // Add API token if configured (for QA/Production environments)
      if (REACT_APP_API_TOKEN) {
        headers['x-api-token'] = REACT_APP_API_TOKEN;
      }

      const response = await fetch(
        `${this.getApiBaseUrl()}/api/transcribe/job-status/${requestId}`,
        {
          method: 'GET',
          headers,
          credentials: 'include', // Include session cookies (for web)
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || errorData.error || 'Failed to fetch job status',
        );
      }

      const result: JobStatusResponse = await response.json();
      return result;
    } catch (error) {
      // Don't log expected errors:
      // - "Job not found" is expected for non-transcription requests
      // - Network errors are expected when offline (polling will resume when online)
      const isExpectedError =
        error instanceof Error &&
        (error.message.includes('Job not found') ||
          error.message.includes('Network error') ||
          error.message.includes('network') ||
          error.message.includes('Failed to fetch') ||
          error.name === 'TypeError'); // Network failure on mobile

      if (!isExpectedError) {
        console.error('❌ getJobStatus error:', error);
      }

      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        'Network error. Please check your connection and try again.',
      );
    }
  }

  /**
   * PERF-4: fire-and-forget telemetry beacon. Reports the device-measured
   * client-perceived timing after the completion notification is shown. This is
   * telemetry, not business data — it lands as a `transcription.perf.client_total`
   * log event on the server. NEVER throws into the UX; failures are swallowed.
   */
  async reportClientTiming(
    jobId: string,
    body: {
      clientTotalMs: number;
      clientUploadMs?: number;
      requestId?: number;
    },
  ): Promise<void> {
    try {
      const authService = getAuthService();
      const jwtToken = await authService.getToken();

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (jwtToken) {
        headers.Authorization = `Bearer ${jwtToken}`;
      }
      if (REACT_APP_API_TOKEN) {
        headers['x-api-token'] = REACT_APP_API_TOKEN;
      }

      await fetch(
        `${this.getApiBaseUrl()}/api/transcribe/jobs/${encodeURIComponent(
          jobId,
        )}/client-timing`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        },
      );
    } catch {
      // Telemetry only — never disrupt the import UX on a failed/offline beacon.
    }
  }

  /**
   * Transcribe audio for an existing request
   * Uploads file, stores previous stage, transitions to PROCESSING
   */
  async transcribeExistingRequest(
    data: TranscribeExistingRequestData,
  ): Promise<CreateRequestWithImportResponse> {
    try {
      // Create FormData for multipart/form-data request
      const formData = new FormData();

      // Append file - use webFile for web platform, otherwise use uri/name/type for mobile
      if (data.file.webFile) {
        // Web platform: use actual File object
        formData.append('file', data.file.webFile);
        formData.append('source', 'web'); // Web uses AWS Batch transcription
      } else {
        // Mobile platform: React Native FormData requires specific format
        const fileForUpload: any = {
          uri: data.file.uri,
          name: data.file.name,
          type: data.file.type,
        };
        formData.append('file', fileForUpload);
        // Mobile: source defaults to null (backward compatible with old mobile apps using streaming)
      }

      // Get JWT token for authorization from the configured auth service
      const authService = getAuthService();
      const jwtToken = await authService.getToken();

      // Prepare headers
      const headers: Record<string, string> = {
        // Don't set Content-Type for multipart/form-data - let fetch set it with boundary
      };

      // Add JWT Authorization header if token exists
      if (jwtToken) {
        headers.Authorization = `Bearer ${jwtToken}`;
      }

      // Add API token if configured (for QA/Production environments)
      if (REACT_APP_API_TOKEN) {
        headers['x-api-token'] = REACT_APP_API_TOKEN;
      }

      // Use the transcription endpoint for existing requests
      const response = await fetch(
        `${this.getApiBaseUrl()}/api/transcribe/${data.requestId}/transcribe`,
        {
          method: 'POST',
          headers,
          credentials: 'include', // Include session cookies (for web)
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            errorData.error ||
            'Failed to transcribe existing request',
        );
      }

      const result: CreateRequestWithImportResponse = await response.json();
      return result;
    } catch (error) {
      console.error('❌ transcribeExistingRequest error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error: Unable to transcribe request');
    }
  }

  /**
   * Create a request from pasted/typed text (no file upload).
   * Sends a JSON { text } payload to the same create endpoint — the paste path
   * is JSON only and never multipart.
   */
  async createRequestFromText(data: {
    customerName: string;
    region: string;
    requestorName: string;
    requestorEmail: string;
    text: string;
    emailThread?: boolean;
  }): Promise<CreateRequestWithImportResponse> {
    return this.postTextIntake(
      `${this.getApiBaseUrl()}/api/transcribe/create-with-import`,
      {
        customerName: data.customerName,
        region: data.region,
        requestorName: data.requestorName,
        requestorEmail: data.requestorEmail,
        text: data.text,
        source: Platform.OS === 'web' ? 'web' : undefined,
        ...(data.emailThread !== undefined
          ? { emailThread: data.emailThread }
          : {}),
      },
      'Failed to create request from text',
    );
  }

  /**
   * Update an existing request from pasted/typed text (no file upload).
   * Server stores previous_stage and transitions to PROCESSING, same as audio.
   */
  async transcribeExistingRequestFromText(data: {
    requestId: number;
    text: string;
    emailThread?: boolean;
  }): Promise<CreateRequestWithImportResponse> {
    return this.postTextIntake(
      `${this.getApiBaseUrl()}/api/transcribe/${data.requestId}/transcribe`,
      {
        text: data.text,
        source: Platform.OS === 'web' ? 'web' : undefined,
        ...(data.emailThread !== undefined
          ? { emailThread: data.emailThread }
          : {}),
      },
      'Failed to update request from text',
    );
  }

  /** Shared JSON POST for text intake. */
  private async postTextIntake(
    url: string,
    payload: Record<string, unknown>,
    errorFallback: string,
  ): Promise<CreateRequestWithImportResponse> {
    try {
      const authService = getAuthService();
      const jwtToken = await authService.getToken();

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (jwtToken) {
        headers.Authorization = `Bearer ${jwtToken}`;
      }
      if (REACT_APP_API_TOKEN) {
        headers['x-api-token'] = REACT_APP_API_TOKEN;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || errorFallback);
      }

      return (await response.json()) as CreateRequestWithImportResponse;
    } catch (error) {
      console.error('❌ text intake error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error: Unable to submit text');
    }
  }
}

// Export singleton instance
export const transcriptionService = new TranscriptionService();
